/**
 * The line as a picture: what a chiller trip in the sub-fab does to the
 * lots on the floor, and what the person on shift can do about it.
 *
 * Every other panel in SimFab is a tool. This one is the building - ten
 * bays, one route, a queue at every tool - and the questions it answers are
 * the ones nobody can answer standing at a tool: how much work is in the
 * line, how long a lot takes, and why a six-hour outage is still being paid
 * for the next evening.
 *
 * THREE POLICIES, ONE SEED. The three curves on each chart are the same
 * arrivals, the same service times and the same failures, differing only in
 * what was decided at two in the morning. The dashed grey line is the same
 * week with no trip at all, and the cost table is measured against it lot
 * by lot, so "dates lost" means dates that the control met.
 *
 * The simulation runs in a worker (LineRunner); this view only draws. It
 * memoizes chart options on the results object so a language change or a
 * tab switch does not repaint eight hundred points for nothing.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { t, tOr } from '../../i18n/i18n'
import { useLang } from '../i18n/LanguageProvider'
import { LineRunner } from '../../sim/line/LineRunner'
import {
  POLICIES,
  QTIME_POLICIES,
  type ChillerTripResult,
  type ExcursionStudy,
  type OperatingPoint,
  type PmPolicy,
  type PmStudy,
  type Policy,
  type QtimePolicy,
  type QtimeResult,
} from '../../sim/line/scenario'
import { LOT_SIZE } from '../../sim/line/line'
import { LINE_FLOW, NEW_TOOLS_MIX } from '../../sim/line/scenario'
import { expand } from '../../sim/flow/model'
import { missingForTags } from '../../learn/floor'
import { ChartView } from '../../ui/kit/ChartView'
import type { ChartOptions, ChartSeries } from '../../ui/Chart'
import { ToggleRow } from '../../ui/kit/controls'
import { Note, Section } from '../../ui/kit/primitives'
import { panelStore } from '../panel/panelStore'

const SEED = 7
const CURVE_LOADS = [0.5, 0.6, 0.7, 0.8, 0.85, 0.9, 0.95]
/** Hours a lot may wait between exposure and etch, in the queue-time tab. */
const QTIME_WINDOW_H = 8
const EXCURSION_RATES = [0.05, 0.1, 0.25, 0.5]
const EXCURSION_SEEDS = [1, 2, 3, 4, 5, 6]
const PM_SEEDS = [1, 2, 3, 4, 5, 6, 7, 8]
const QT_COLOUR: Record<QtimePolicy, string> = { run: '#8ab4f8', gate: '#7ee0a0' }

/** One colour per policy, the same on every chart and in the table. */
const COLOUR: Record<Policy, string> = {
  reroute: '#8ab4f8',
  hold: '#f0a04b',
  'by-date': '#7ee0a0',
}
const CONTROL_COLOUR = 'rgba(200, 208, 220, 0.55)'

const policyLabel = (p: Policy): string =>
  p === 'reroute'
    ? tOr('line.PolicyReroute', 'Reroute')
    : p === 'hold'
      ? tOr('line.PolicyHold', 'Hold starts')
      : tOr('line.PolicyByDate', 'By date')

const policyNote = (p: Policy): string =>
  p === 'reroute'
    ? tOr('line.PolicyRerouteNote', 'Everything queues for the surviving chamber, first come first served. What happens if nobody decides.')
    : p === 'hold'
      ? tOr('line.PolicyHoldNote', 'Stop starting lots until the chambers are back.')
      : tOr('line.PolicyByDateNote', 'Etch runs whoever is nearest their due date next, hot or not.')

type Tab = 'scenario' | 'curve' | 'qtime' | 'excursion' | 'pm'

const qtimeLabel = (p: QtimePolicy): string => (p === 'run' ? tOr('line.QtimeRun', 'Let it run') : tOr('line.QtimeGate', 'Gate the scanner'))
const pmLabel = (p: PmPolicy): string => (p === 'now' ? tOr('line.PmNow', 'Service it now') : tOr('line.PmDefer', 'Run on'))

export function LineView() {
  useLang()
  const runner = useRef<LineRunner | null>(null)
  const [load, setLoad] = useState<'0.85' | '0.95'>('0.85')
  const [call, setCall] = useState<'6' | '12'>('6')
  const [products, setProducts] = useState<'1' | '2' | '3'>('1')
  const [tab, setTab] = useState<Tab>('scenario')
  const [results, setResults] = useState<Record<Policy, ChillerTripResult> | null>(null)
  const [curve, setCurve] = useState<OperatingPoint[] | null>(null)
  const [running, setRunning] = useState(false)
  // The three studies, each cached by the load it was run at and run only
  // when its tab is first opened: an excursion study is twenty-four
  // two-month runs, and nobody should pay for it to look at the trip.
  const [qtime, setQtime] = useState<{ load: number; results: Record<QtimePolicy, QtimeResult> } | null>(null)
  const [excursion, setExcursion] = useState<{ load: number; studies: ExcursionStudy[] } | null>(null)
  const [pm, setPm] = useState<{ load: number; studies: Record<PmPolicy, PmStudy> } | null>(null)

  useEffect(() => {
    runner.current = new LineRunner()
    return () => {
      runner.current?.dispose()
      runner.current = null
    }
  }, [])

  // The scenario, whenever the two knobs change. A superseded request
  // resolves to null and is dropped without touching the screen.
  useEffect(() => {
    const r = runner.current
    if (!r) return
    setRunning(true)
    r.trip(Number(load), Number(call), SEED, products === '3' ? 'three' : products === '2' ? 'two' : 'one').then((res) => {
      if (!res) return
      setResults(res)
      setRunning(false)
    })
  }, [load, call, products])

  useEffect(() => {
    const r = runner.current
    if (!r) return
    const l = Number(load)
    if (tab === 'qtime' && qtime?.load !== l) r.qtime(l, QTIME_WINDOW_H, SEED).then((res) => res && setQtime({ load: l, results: res }))
    if (tab === 'excursion' && excursion?.load !== l) r.excursion(EXCURSION_RATES, EXCURSION_SEEDS, l).then((res) => res && setExcursion({ load: l, studies: res }))
    if (tab === 'pm' && pm?.load !== l) r.pm(PM_SEEDS, l).then((res) => res && setPm({ load: l, studies: res }))
  }, [tab, load, qtime?.load, excursion?.load, pm?.load])

  // The curve once: it does not depend on the knobs.
  useEffect(() => {
    const r = runner.current
    if (!r) return
    r.curve(CURVE_LOADS, SEED).then((pts) => {
      if (pts) setCurve(pts)
    })
  }, [])

  const wipChart = useMemo(() => (results ? wipOptions(results) : null), [results])
  const ctChart = useMemo(() => (results ? ctOptions(results) : null), [results])
  const curveChart = useMemo(() => (curve ? curveOptions(curve, Number(load)) : null), [curve, load])
  const qtimeChart = useMemo(() => (qtime && qtime.load === Number(load) ? qtimeOptions(qtime.results) : null), [qtime, load])
  const excursionChart = useMemo(() => (excursion && excursion.load === Number(load) ? excursionOptions(excursion.studies) : null), [excursion, load])

  const any = results ? results.reroute : null

  // The line is the full product route on the reference floor. On a floor
  // somebody built without some of its stations, that is still the line
  // being simulated - and the first thing said is which stations this
  // floor would have to send a lot elsewhere for.
  const missingHere = useMemo(
    () => missingForTags([LINE_FLOW, ...(products === '3' ? NEW_TOOLS_MIX.map((p) => p.flow) : [])].flatMap((f) => expand(f).map((s) => s.tool))),
    [products],
  )

  return (
    <div className="line-view">
      {missingHere.length > 0 && (
        <Note>{t('author.LineNotOnFloor', { tags: missingHere.join(', ') })}</Note>
      )}
      <Note>
        {tOr(
          'line.Intro',
          'Ten bays, one route, a queue at every tool. At two in the morning a chiller in the sub-fab ' +
            'trips: the process cooling water runs warm and two of the three chambers on every etcher ' +
            'lose temperature control. The third keeps running. Lots keep arriving. You are on shift.',
        )}
      </Note>
      {any && (
        <div className="line-head">
          {t('line.Bottleneck', {
            tag: any.bottleneck,
            n: any.scale,
            starts: Math.round(any.little.throughputPerH * LOT_SIZE * 24),
          })}
        </div>
      )}
      <div className="line-controls">
        <div>
          <div className="line-control-label">{tOr('line.LoadLabel', 'Line load')}</div>
          <ToggleRow
            value={load}
            onChange={(v) => setLoad(v as '0.85' | '0.95')}
            options={[
              { value: '0.85', label: tOr('line.Load85', '85% — a healthy line') },
              { value: '0.95', label: tOr('line.Load95', '95% — a full one') },
            ]}
          />
        </div>
        <div>
          <div className="line-control-label">{tOr('line.CallLabel', 'The call to facilities')}</div>
          <ToggleRow
            value={call}
            onChange={(v) => setCall(v as '6' | '12')}
            options={[
              { value: '6', label: tOr('line.CallPrompt', 'made at once — chambers back in 6 h') },
              { value: '12', label: tOr('line.CallLate', 'made at 08:00 — chambers back in 12 h') },
            ]}
          />
        </div>
        <div>
          <div className="line-control-label">{tOr('line.ProductsLabel', 'Products')}</div>
          <ToggleRow
            value={products}
            onChange={(v) => setProducts(v as '1' | '2' | '3')}
            options={[
              { value: '1', label: tOr('line.ProductsOne', 'one') },
              { value: '2', label: tOr('line.ProductsTwo', 'two — 30% double-patterned') },
              { value: '3', label: tOr('line.ProductsThree', 'three — 25% EUV, 3% qubit chips') },
            ]}
          />
        </div>
        <div>
          <div className="line-control-label">{tOr('line.ViewLabel', 'View')}</div>
          <ToggleRow
            value={tab}
            onChange={(v) => setTab(v as Tab)}
            options={[
              { value: 'scenario', label: tOr('line.TabScenario', 'The chiller trip') },
              { value: 'qtime', label: tOr('line.TabQtime', 'The queue-time window') },
              { value: 'excursion', label: tOr('line.TabExcursion', 'The excursion') },
              { value: 'pm', label: tOr('line.TabPm', 'The maintenance') },
              { value: 'curve', label: tOr('line.TabCurve', 'The operating curve') },
            ]}
          />
        </div>
      </div>

      {tab === 'scenario' && (
        <>
          <div className="line-legend">
            {POLICIES.map((p) => (
              <span key={p} className="line-legend-item">
                <span className="line-swatch" style={{ background: COLOUR[p] }} />
                <b>{policyLabel(p)}</b> {policyNote(p)}
              </span>
            ))}
            <span className="line-legend-item">
              <span className="line-swatch line-swatch-dashed" />
              <b>{tOr('line.Control', 'No trip')}</b> {tOr('line.ControlNote', 'the same week, with the chiller running.')}
            </span>
          </div>
          <div className="line-charts">
            {wipChart ? <ChartView width={520} height={250} options={wipChart} /> : <div className="line-wait">{tOr('line.Running', 'Running the line…')}</div>}
            {ctChart ? <ChartView width={520} height={250} options={ctChart} /> : <div className="line-wait" />}
          </div>
          <DecideRow
            scenario="chiller"
            label={tOr('line.DecideLabel', 'Your call, while the chambers are down')}
            choices={POLICIES.map((p) => ({ id: p, label: policyLabel(p) }))}
            load={Number(load)}
            restoreH={Number(call)}
          />
          {results && <CostTable results={results} restoreH={Number(call)} running={running} />}
          {results && products === '3' && <NewToolsTable result={results.reroute} running={running} />}
          {any && (
            <Section title={tOr('line.LittleTitle', "Little's law on the steady line")}>
              <Note>
                {t('line.LittleLine', {
                  wip: any.little.wip.toFixed(0),
                  thr: any.little.throughputPerH.toFixed(2),
                  ct: any.little.ctH.toFixed(0),
                  ratio: any.little.ratio.toFixed(2),
                })}
              </Note>
            </Section>
          )}
          <Section title={tOr('line.FindingsTitle', 'What the line shows')}>
            <Note>
              {tOr(
                'line.FindingDrain',
                'The chambers are back in hours; the queue is not. Every lot that waited at etch is now late ' +
                  'for its next tool, and the ones behind it are late for theirs, and the line spends its spare ' +
                  'capacity - what is left after the arrivals it already has - working that off. At 85% there ' +
                  'is spare capacity. At 95% there is almost none, and the same six hours take days to clear.',
              )}
            </Note>
            <Note>
              {tOr(
                'line.FindingByDate',
                'Running etch by due date loses the fewest dates at either load, because nearest-date is close ' +
                  'to oldest-first and oldest-first pushes lots out of a re-entrant line instead of round it ' +
                  'again. Its price shows only at 95%: hot lots stop jumping a queue that is now long, and most ' +
                  'of them miss. The rule that is right at 85% is the rule that loses the hot lot at 95%.',
              )}
            </Note>
            <Note>
              {tOr(
                'line.FindingHold',
                'Holding starts changes nothing at etch. The lots that will pile up there over the next six ' +
                  'hours are already in the building; the lots you did not start were eight days from etch. ' +
                  'All a hold does is take six hours of slack from the lots it held.',
              )}
            </Note>
          </Section>
        </>
      )}

      {tab === 'qtime' && (
        <>
          <Note>
            {tOr(
              'line.QtimeNote',
              'The same trip, on a route where the resist has a shelf life: a lot not etched within eight hours of ' +
                'leaving the scanner goes back to be stripped and exposed again. Every lot exposed and then stuck behind ' +
                'a chamber that is down is a lot whose clock is running. Two moves: let it run, or gate the scanner - ' +
                'do not expose a lot that the etcher it is bound for could not etch within the window, so it waits before the clock starts.',
            )}
          </Note>
          <div className="line-charts">
            {qtimeChart ? <ChartView width={560} height={260} options={qtimeChart} frameStyle={{ flex: '0 1 620px', maxWidth: 620 }} /> : <div className="line-wait">{tOr('line.Running', 'Running the line…')}</div>}
          </div>
          <DecideRow
            scenario="qtime"
            label={tOr('line.DecideLabelQtime', 'Your call, at the scanner')}
            choices={QTIME_POLICIES.map((p) => ({ id: p, label: qtimeLabel(p) }))}
            load={Number(load)}
            restoreH={Number(call)}
          />
          {qtime && qtime.load === Number(load) && (
            <table className="line-table">
              <thead>
                <tr>
                  <th>{tOr('line.ColPolicy', 'Policy')}</th>
                  <th>{tOr('line.ColReworks', 'Lots sent back')}</th>
                  <th>{tOr('line.ColReworksControl', 'With no trip')}</th>
                  <th>{tOr('line.ColReworkHours', 'Scanner-hours lost')}</th>
                  <th>{tOr('line.ColDelayed', 'Lots delayed')}</th>
                  <th>{tOr('line.ColDates', 'Dates lost')}</th>
                </tr>
              </thead>
              <tbody>
                {QTIME_POLICIES.map((p) => {
                  const r = qtime.results[p]
                  return (
                    <tr key={p}>
                      <td>
                        <span className="line-swatch" style={{ background: QT_COLOUR[p] }} /> {qtimeLabel(p)}
                      </td>
                      <td>{r.reworks}</td>
                      <td>{r.reworksControl}</td>
                      <td>{r.reworkHours.toFixed(0)}</td>
                      <td>{`${r.lotsDelayed} / ${r.lotsOut}`}</td>
                      <td>{r.lateCaused}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
          <Note>
            {tOr(
              'line.QtimeFinding',
              'At 85% the gate prevents most of the trip\'s reworks and costs almost no dates: the lots it holds are ' +
                'at the scanner, whose queue costs nothing but time. At 95% look at the no-trip column first - a ' +
                'queue-time window on a line that full is a rework machine before anything goes wrong, because the ' +
                'etch queue regularly waits past the window on its own. The gate still takes a bite out of the trip. ' +
                'This is the one case where holding lots is right, and only because it holds them before the clock starts.',
            )}
          </Note>
        </>
      )}

      {tab === 'excursion' && (
        <>
          <Note>
            {tOr(
              'line.ExcursionNote',
              'No trip this time. At two in the morning one deposition tool goes silently out of control and keeps ' +
                'running. Nothing on the floor says so. Metrology inspects a fraction of its visits; an inspected lot ' +
                'that went through the bad tool is what catches it. Every lot through the tool until then is exposed. ' +
                'Averaged over six weeks with different luck, because one detection is one draw.',
            )}
          </Note>
          <div className="line-charts">
            {excursionChart ? <ChartView width={560} height={260} options={excursionChart} frameStyle={{ flex: '0 1 620px', maxWidth: 620 }} /> : <div className="line-wait">{tOr('line.Running', 'Running the line…')}</div>}
          </div>
          <DecideRow
            scenario="excursion"
            label={tOr('line.DecideLabelExcursion', 'Your call: how many metrology visits get inspected')}
            choices={EXCURSION_RATES.map((r) => ({ id: String(r), label: `${Math.round(100 * r)}%` }))}
            load={Number(load)}
          />
          {excursion && excursion.load === Number(load) && (
            <table className="line-table">
              <thead>
                <tr>
                  <th>{tOr('line.ColRate', 'Visits inspected')}</th>
                  <th>{tOr('line.ColDetect', 'Caught after')}</th>
                  <th>{tOr('line.ColExposed', 'Lots exposed, mean')}</th>
                  <th>{tOr('line.ColExposedWorst', 'Worst week')}</th>
                  <th>{tOr('line.ColInspections', 'Inspections a day')}</th>
                  <th>{tOr('line.ColMetWait', 'Wait at MET-05')}</th>
                  <th>{tOr('line.ColMetUtil', 'MET-05 busy')}</th>
                  <th>{tOr('line.ColCt', 'Cycle time')}</th>
                </tr>
              </thead>
              <tbody>
                {excursion.studies.map((st) => (
                  <tr key={st.rate}>
                    <td>{`${Math.round(100 * st.rate)}%`}</td>
                    <td>{t('line.Hours', { h: st.meanDelayH.toFixed(1) })}</td>
                    <td>{st.meanExposed.toFixed(1)}</td>
                    <td>{st.worstExposed}</td>
                    <td>{st.inspectionsPerDay.toFixed(0)}</td>
                    <td>{t('line.Hours', { h: st.meanMetWaitH.toFixed(2) })}</td>
                    <td>{`${Math.round(100 * st.metUtilisation)}%`}</td>
                    <td>{t('line.Hours', { h: st.meanCtH.toFixed(0) })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Note>
            {tOr(
              'line.ExcursionFinding',
              'Lots exposed is throughput through the bad tool times the hours until it is caught - Little\'s law from ' +
                'the yield side. Sampling ten times harder catches it in half the time and halves the lots at risk, ' +
                'and it fills the metrology tool: its queue grows with the rate and every lot in the building pays ' +
                'in cycle time. The sampling rate is a decision about which of those two costs the fab would rather carry.',
            )}
          </Note>
        </>
      )}

      {tab === 'pm' && (
        <>
          <Note>
            {tOr(
              'line.PmNote',
              'One chamber of one etcher is due its maintenance at two in the morning. Do it now: four planned hours, ' +
                'tonight. Or run on: a worn chamber that is going to fail, and when it does, the unplanned repair takes ' +
                'twice as long, at an hour nobody chose. A deferral is a bet, so both are judged over eight weeks with ' +
                'different luck, against the same weeks with neither.',
            )}
          </Note>
          <DecideRow
            scenario="pm"
            label={tOr('line.DecideLabelPm', 'Your call, on the due chamber')}
            choices={(['now', 'defer'] as PmPolicy[]).map((p) => ({ id: p, label: pmLabel(p) }))}
            load={Number(load)}
          />
          {pm && pm.load === Number(load) ? (
            <table className="line-table">
              <thead>
                <tr>
                  <th>{tOr('line.ColPolicy', 'Policy')}</th>
                  <th>{tOr('line.ColChamberDown', 'Chamber down, mean week')}</th>
                  <th>{tOr('line.ColBeyondControl', 'Beyond a week with neither')}</th>
                  <th>{tOr('line.ColWorstWeek', 'Worst week')}</th>
                  <th>{tOr('line.ColFailed', 'Failed before it was serviced')}</th>
                  <th>{tOr('line.ColDelayLotHours', 'Lot-hours of delay')}</th>
                  <th>{tOr('line.ColDates', 'Dates lost')}</th>
                </tr>
              </thead>
              <tbody>
                {(['now', 'defer'] as PmPolicy[]).map((p) => {
                  const st = pm.studies[p]
                  return (
                    <tr key={p}>
                      <td>{pmLabel(p)}</td>
                      <td>{t('line.Hours', { h: st.meanDownH.toFixed(1) })}</td>
                      <td>{t('line.Hours', { h: (st.meanDownH - st.meanControlDownH).toFixed(1) })}</td>
                      <td>{t('line.Hours', { h: st.worstDownH.toFixed(1) })}</td>
                      <td>{`${st.failures} / ${st.seeds.length}`}</td>
                      <td>{st.meanNetDelayLotHours.toFixed(0)}</td>
                      <td>{st.meanLateCaused.toFixed(1)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          ) : (
            <div className="line-wait">{tOr('line.Running', 'Running the line…')}</div>
          )}
          {pm && pm.load === Number(load) && (
            <Note>
              {t('line.PmFinding', {
                pm: pm.studies.now.pmHours,
                worn: pm.studies.defer.wornMtbfH,
                repair: pm.studies.defer.wornMttrH,
                mttr: pm.studies.defer.mttrH,
              })}
            </Note>
          )}
        </>
      )}

      {tab === 'curve' && (
        <>
          <Note>
            {tOr(
              'line.CurveNote',
              'Cycle time against load on the healthy line, with no trip. Flat and near the raw process time when ' +
                'the line is half empty, bending through the eighties, steep in the nineties. The chiller trip at ' +
                '85% and at 95% is two points on this curve seen from inside: the slope is how long a queue takes ' +
                'to clear once it has formed.',
            )}
          </Note>
          <div className="line-charts">
            {curveChart ? (
              <ChartView width={560} height={280} options={curveChart} frameStyle={{ flex: '0 1 620px', maxWidth: 620 }} />
            ) : (
              <div className="line-wait">{tOr('line.Running', 'Running the line…')}</div>
            )}
          </div>
          {curve && (
            <table className="line-table">
              <thead>
                <tr>
                  <th>{tOr('line.ColLoad', 'Load')}</th>
                  <th>{tOr('line.ColCt', 'Cycle time')}</th>
                  <th>{tOr('line.ColCtRaw', '× raw process time')}</th>
                  <th>{tOr('line.ColWip', 'Lots in the line')}</th>
                </tr>
              </thead>
              <tbody>
                {curve.map((p) => (
                  <tr key={p.load}>
                    <td>{`${Math.round(100 * p.load)}%`}</td>
                    <td>{`${p.ctH.toFixed(0)} h`}</td>
                    <td>{p.ctOverRaw.toFixed(2)}</td>
                    <td>{p.wip.toFixed(0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  )
}

/**
 * EUV-22, SEM-23 and QDT-24 on the line, beside the bottleneck.
 *
 * Shown only for the mix that carries them. The point of the table is the
 * last column read against the third: the fridge farm runs at half its
 * capacity, far below the etcher, and makes its lots wait longest, because
 * what a lot waits for is a fridge to come free, and a fridge is busy for two
 * days at a time.
 */
const NEW_TOOL_TAGS = ['ETCH-01', 'EUV-22', 'SEM-23', 'QDT-24']

function NewToolsTable({ result, running }: { result: ChillerTripResult; running: boolean }) {
  const rows = NEW_TOOL_TAGS.map((tag) => result.tools.find((x) => x.tag === tag)).filter((x): x is NonNullable<typeof x> => !!x)
  if (rows.length < 2) return null
  return (
    <Section title={tOr('line.NewToolsTitle', 'The scanner, the CD-SEM and the fridge farm, beside the bottleneck')}>
      <table className={`line-table${running ? ' line-table-stale' : ''}`}>
        <thead>
          <tr>
            <th>{tOr('line.ColTool', 'Tool')}</th>
            <th>{tOr('line.ColInBuilding', 'In the building')}</th>
            <th>{tOr('line.ColUtilisation', 'Busy')}</th>
            <th>{tOr('line.ColDown', 'Down')}</th>
            <th>{tOr('line.ColWait', 'Mean wait for it')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.tag}>
              <td>{r.tag}</td>
              <td>{r.tools}</td>
              <td>{`${(100 * r.utilisation).toFixed(0)}%`}</td>
              <td>{`${(100 * r.downtime).toFixed(1)}%`}</td>
              <td>{t('line.Hours', { h: r.meanWaitH.toFixed(1) })}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Note>
        {tOr(
          'line.NewToolsNote',
          'A quarter of the starts are a leading-edge part: EUV-22 prints its gate and four lowest metals, and SEM-23 measures every one of those exposures before ETCH-01 is allowed to etch it. Three per cent are qubit chips, which leave the front end after eight operations and then spend two days in a fridge. Read the last column against the third. The fridge farm runs at about half its capacity, far below the etcher, and its lots wait the longest by an order of magnitude: what a lot waits for is a fridge to come free, and a fridge is busy for two days at a time. Waiting scales with how long the job in front of you takes, not only with how busy the tool is. The scanner is the other lesson. The building has one, it is down one hour in eight, and there is nowhere else to send the lot. The trip stops all three water-cooled families at once: two etch chambers in three, the scanner, and every fridge.',
        )}
      </Note>
    </Section>
  )
}

function CostTable({ results, restoreH, running }: { results: Record<Policy, ChillerTripResult>; restoreH: number; running: boolean }) {
  return (
    <Section title={tOr('line.CostTitle', 'What the fault cost, against the same week with no trip')}>
      <table className={`line-table${running ? ' line-table-stale' : ''}`}>
        <thead>
          <tr>
            <th>{tOr('line.ColPolicy', 'Policy')}</th>
            <th>{tOr('line.ColPeak', 'Lots at etch, peak')}</th>
            <th>{tOr('line.ColDrain', 'Queue back to normal')}</th>
            <th>{tOr('line.ColDelayed', 'Lots delayed')}</th>
            <th>{tOr('line.ColMeanDelay', 'Mean delay')}</th>
            <th>{tOr('line.ColDates', 'Dates lost')}</th>
            <th>{tOr('line.ColHot', 'Hot lots lost')}</th>
            <th>{tOr('line.ColChamberHours', 'Chamber-hours lost')}</th>
          </tr>
        </thead>
        <tbody>
          {POLICIES.map((p) => {
            const r = results[p]
            return (
              <tr key={p}>
                <td>
                  <span className="line-swatch" style={{ background: COLOUR[p] }} /> {policyLabel(p)}
                </td>
                <td>{`${r.baselineWip.toFixed(0)} → ${r.peakWip}`}</td>
                <td>{r.drainH === null ? tOr('line.DrainNever', 'not within two weeks') : t('line.HoursAfter', { h: r.drainH.toFixed(0) })}</td>
                <td>{`${r.lotsDelayed} / ${r.lotsOut}`}</td>
                <td>{t('line.Hours', { h: r.meanDelayH.toFixed(1) })}</td>
                <td>{r.lateCaused}</td>
                <td>{`${r.hotMissedCaused} / ${r.hotOut}`}</td>
                <td>{r.chamberHoursLost}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <Note>{t('line.CostNote', { h: restoreH })}</Note>
    </Section>
  )
}

/**
 * The decision, journaled.
 *
 * The work order "Two rooms from the symptom" asks the student to decide
 * what the line does while the chambers are down, with this picture in
 * front of them. Any of the three counts: the order is about deciding with
 * the numbers rather than guessing at the tool, and the debrief says what
 * each choice cost against the others. The event carries the load and the
 * restore time the student was looking at, so an instructor reading the
 * journal knows which picture the decision was made on.
 */
function DecideRow({
  scenario,
  choices,
  label,
  load,
  restoreH,
}: {
  scenario: string
  choices: Array<{ id: string; label: string }>
  label: string
  load: number
  restoreH?: number
}) {
  const [chosen, setChosen] = useState<string | null>(() => {
    const j = panelStore.services?.journal
    const last = j?.events.filter((e) => e.kind === 'lineDecision' && (e.scenario ?? 'chiller') === scenario).pop()
    return last && last.kind === 'lineDecision' ? last.policy : null
  })
  const decide = (id: string) => {
    setChosen(id)
    panelStore.services?.journal.record({ kind: 'lineDecision', scenario, policy: id, load, restoreH })
  }
  const chosenLabel = choices.find((c) => c.id === chosen)?.label
  return (
    <div className="line-decide">
      <span className="line-control-label">{label}</span>
      <div className="btn-row">
        {choices.map((c) => (
          <button key={c.id} className={`btn${chosen === c.id ? ' primary' : ''}`} onClick={() => decide(c.id)}>
            {c.label}
          </button>
        ))}
      </div>
      <span className="line-decided">
        {chosenLabel ? t('line.Decided', { policy: chosenLabel }) : tOr('line.Undecided', 'Not yet decided. The order needs one; the numbers below are the same either way.')}
      </span>
    </div>
  )
}

// ------------------------------------------------------------- charts

/** A policy's sampled series, as hours from the trip. */
function series(r: ChillerTripResult, field: 'bottleneckWip' | 'ctLast24h', from: number): Array<[number, number]> {
  const out: Array<[number, number]> = []
  for (const s of r.samples) {
    if (s.t < from) continue
    const v = s[field]
    if (Number.isNaN(v)) continue
    out.push([s.t - r.tripAt, v])
  }
  return out
}

function marks(r: { tripAt: number; restoreAt: number }) {
  return [
    { x: 0, label: tOr('line.MarkTrip', 'chiller trips'), colour: 'rgba(240, 96, 96, 0.8)' },
    { x: r.restoreAt - r.tripAt, label: tOr('line.MarkRestore', 'chiller back'), colour: 'rgba(126, 224, 160, 0.8)' },
  ]
}

/**
 * Lots at etch BEYOND the control, hour by hour.
 *
 * Plotted as a difference on purpose. The etch queue of a healthy line at
 * 85% swings between forty and a hundred and forty lots on its own - a
 * chamber fails here, a boat fills there - and a six-hour outage's peak of a
 * hundred and thirty sits inside that swing, invisible. The control shares
 * every one of those failures and boats, so subtracting it leaves exactly
 * what the trip did and nothing else. That is the one thing a run without
 * common random numbers could not draw.
 */
function wipOptions(results: Record<Policy, ChillerTripResult>): ChartOptions {
  const any = results.reroute
  const from = any.tripAt - 24
  const to = any.tripAt + 120
  const control = new Map<number, number>()
  for (const s of any.controlSamples) control.set(s.t, s.bottleneckWip)
  const s: ChartSeries[] = POLICIES.map((p) => {
    const pts: Array<[number, number]> = []
    for (const smp of results[p].samples) {
      if (smp.t < from || smp.t > to) continue
      const c = control.get(smp.t)
      if (c === undefined) continue
      pts.push([smp.t - any.tripAt, smp.bottleneckWip - c])
    }
    return { label: policyLabel(p), points: pts, colour: COLOUR[p] }
  })
  return {
    title: tOr('line.ChartWipTitle', 'Lots at etch beyond the no-trip line'),
    xLabel: tOr('line.ChartX', 'hours after the trip'),
    yLabel: tOr('line.ChartWipY', 'lots'),
    series: s,
    xMin: -24,
    xMax: 120,
    annotations: [...marks(any), { y: 0, label: tOr('line.Control', 'No trip'), colour: CONTROL_COLOUR }],
    format: (x, y) => `${x.toFixed(0)} h: ${y > 0 ? '+' : ''}${y.toFixed(0)}`,
  }
}

/**
 * Cycle time of the lots finishing, day by day. One product: a line per
 * policy over the control. Two products: the default policy's line per
 * product over each product's control - the picture a fab actually reads,
 * where the same outage lands on a long route and a short one differently.
 */
function ctOptions(results: Record<Policy, ChillerTripResult>): ChartOptions {
  const any = results.reroute
  const from = any.tripAt - 48
  const two = any.rawHoursByProduct.length > 1
  const s: ChartSeries[] = []
  if (two) {
    // Two products is the double-patterned mix; three is the EUV part and the qubit chip.
    const names =
      any.rawHoursByProduct.length > 2
        ? [tOr('line.ProductA', 'single-patterned'), tOr('line.ProductEuv', 'EUV part'), tOr('line.ProductQuantum', 'qubit chip')]
        : [tOr('line.ProductA', 'single-patterned'), tOr('line.ProductB', 'double-patterned')]
    const cols = ['#8ab4f8', '#f0a04b', '#7ee0a0']
    any.rawHoursByProduct.forEach((_, p) => {
      const pts: Array<[number, number]> = []
      for (const smp of any.samples) {
        const v = smp.ctLast24hByProduct?.[p]
        if (smp.t < from || v === undefined || Number.isNaN(v)) continue
        pts.push([smp.t - any.tripAt, v])
      }
      const ctrl: Array<[number, number]> = []
      for (const smp of any.controlSamples) {
        const v = smp.ctLast24hByProduct?.[p]
        if (smp.t < from || v === undefined || Number.isNaN(v)) continue
        ctrl.push([smp.t - any.tripAt, v])
      }
      s.push({ label: names[p], points: pts, colour: cols[p] })
      // Three products is six lines: the legend names the products and the
      // title says what the dashes are, or the legend runs off the chart.
      s.push({ label: names.length > 2 ? '' : `${names[p]}, ${tOr('line.Control', 'No trip').toLowerCase()}`, points: ctrl, colour: cols[p], dashed: true })
    })
  } else {
    for (const p of POLICIES) s.push({ label: policyLabel(p), points: series(results[p], 'ctLast24h', from), colour: COLOUR[p] })
    s.push({ label: tOr('line.Control', 'No trip'), points: controlSeries(any, 'ctLast24h', from), colour: CONTROL_COLOUR, dashed: true })
  }
  return {
    title: any.rawHoursByProduct.length > 2 ? tOr('line.ChartCtTitleMix', 'Cycle time by product, under reroute (dashed: no trip)') : two ? tOr('line.ChartCtTitleProducts', 'Cycle time by product, under reroute') : tOr('line.ChartCtTitle', 'Cycle time of the lots finishing, day by day'),
    xLabel: tOr('line.ChartX', 'hours after the trip'),
    yLabel: tOr('line.ChartCtY', 'hours'),
    series: s,
    xMin: -48,
    annotations: marks(any),
    format: (x, y) => `${x.toFixed(0)} h: ${y.toFixed(0)} h`,
  }
}

function controlSeries(r: ChillerTripResult, field: 'bottleneckWip' | 'ctLast24h', from: number): Array<[number, number]> {
  const out: Array<[number, number]> = []
  for (const s of r.controlSamples) {
    if (s.t < from) continue
    const v = s[field]
    if (Number.isNaN(v)) continue
    out.push([s.t - r.tripAt, v])
  }
  return out
}

/** Lots sent back to the scanner, cumulative from the trip, under each move. */
function qtimeOptions(results: Record<QtimePolicy, QtimeResult>): ChartOptions {
  const any = results.run
  const from = any.tripAt - 24
  const to = any.endAt
  const series: ChartSeries[] = QTIME_POLICIES.map((p) => {
    const r = results[p]
    const base = r.samples.find((s) => s.t >= any.tripAt)?.reworks ?? 0
    const pts: Array<[number, number]> = []
    for (const s of r.samples) {
      if (s.t < from || s.t > to) continue
      pts.push([s.t - any.tripAt, s.reworks - base])
    }
    return { label: qtimeLabel(p), points: pts, colour: QT_COLOUR[p] }
  })
  const cb = any.controlSamples.find((s) => s.t >= any.tripAt)?.reworks ?? 0
  const control: Array<[number, number]> = []
  for (const s of any.controlSamples) {
    if (s.t < from || s.t > to) continue
    control.push([s.t - any.tripAt, s.reworks - cb])
  }
  series.push({ label: tOr('line.Control', 'No trip'), points: control, colour: CONTROL_COLOUR, dashed: true })
  return {
    title: tOr('line.QtimeChartTitle', 'Lots sent back to the scanner, since the trip'),
    xLabel: tOr('line.ChartX', 'hours after the trip'),
    yLabel: tOr('line.ChartWipY', 'lots'),
    series,
    xMin: -24,
    yMin: 0,
    annotations: marks(any),
    format: (x, y) => `${x.toFixed(0)} h: ${y.toFixed(0)}`,
  }
}

/** Lots exposed and the metrology wait, against the sampling rate. */
function excursionOptions(studies: ExcursionStudy[]): ChartOptions {
  return {
    title: tOr('line.ExcursionChartTitle', 'What the sampling rate buys, and costs'),
    xLabel: tOr('line.ExcursionChartX', 'fraction of metrology visits inspected'),
    yLabel: tOr('line.ExcursionChartY', 'lots · wait × 10 h'),
    series: [
      { label: tOr('line.ExcursionSeriesExposed', 'lots exposed, mean'), points: studies.map((s) => [s.rate, s.meanExposed]), colour: '#f0a04b', markers: true },
      { label: tOr('line.ExcursionSeriesWait', 'wait at MET-05, × 10 h'), points: studies.map((s) => [s.rate, 10 * s.meanMetWaitH]), colour: '#8ab4f8', markers: true },
    ],
    xMin: 0,
    xMax: 0.55,
    yMin: 0,
    format: (x, y) => `${Math.round(100 * x)}%: ${y.toFixed(1)}`,
  }
}

function curveOptions(curve: OperatingPoint[], load: number): ChartOptions {
  return {
    title: tOr('line.CurveTitle', 'Cycle time against load'),
    xLabel: tOr('line.CurveX', 'load on the bottleneck'),
    yLabel: tOr('line.CurveY', '× raw process time'),
    series: [{ label: tOr('line.CurveSeries', 'healthy line'), points: curve.map((p) => [p.load, p.ctOverRaw]), colour: '#8ab4f8', markers: true }],
    xMin: 0.45,
    xMax: 1,
    yMin: 1,
    annotations: [{ x: load, label: tOr('line.CurveHere', 'this line'), colour: 'rgba(240, 160, 75, 0.8)' }],
    format: (x, y) => `${Math.round(100 * x)}%: ${y.toFixed(2)}×`,
  }
}
